import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { ConfirmDialog, DialogModal, Drawer } from "@zfp-akademie-a-s/zfp-design";
import { FormSelect, FormTextField } from "@zfp-akademie-a-s/zfp-design/form";
import { FormDatePicker } from "@zfp-akademie-a-s/zfp-design/form/date-picker";
import { FormNumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import { type LiabilityFormType, getLiabilityFormSchema } from "Forms/Schemas/Assets/liabilityFormSchema";
import { type Option, toSelectOptions } from "Forms/fieldOptions";
import {
  getGetAssetsOverviewQueryKey,
  getGetLiabilityItemQueryKey,
  getGetLoanExpenseQueryKey,
  getListLiabilityItemsQueryKey,
  useCreateLiabilityItem,
  useDeleteAssetItem,
  useGetAnalysisClients,
  useGetLiabilityItem,
  useGetLoanExpense,
  useUpdateLiabilityItem,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, type LiabilityItem, LiabilityItemLiabilitySubType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { LIABILITY_SUBTYPE_RESOURCE_KEY } from "Infrastructure/Utils/AssetUtils";
import { formatClientName } from "Infrastructure/Utils/ClientUtils";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { type FC, useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { NOTE_MAX_LENGTH } from "../constants";

type Props = {
  readonly onClose: () => void;
  readonly liability?: LiabilityItem;
  readonly open: boolean;
};

export const LiabilityDrawer: FC<Props> = ({ onClose, open, liability }) => {
  const { readOnly, analysisId, versionId } = useContext(AnalysisDetailContext);
  const { t } = useResource();
  const queryClient = useQueryClient();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const { data: liabilityDetail } = useGetLiabilityItem(analysisId, versionId, liability?.guid || "", {
    query: { enabled: !!liability?.guid },
  });
  const { data: loanExpense } = useGetLoanExpense(analysisId, versionId);
  const { data: clientsData } = useGetAnalysisClients(analysisId, versionId);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getGetAssetsOverviewQueryKey(analysisId, versionId) });
    queryClient.invalidateQueries({ queryKey: getListLiabilityItemsQueryKey(analysisId, versionId) });
    queryClient.invalidateQueries({ queryKey: getGetLoanExpenseQueryKey(analysisId, versionId) });
  };

  const { mutate: create } = useCreateLiabilityItem();
  const { mutate: update } = useUpdateLiabilityItem();
  const { mutate: deleteItem } = useDeleteAssetItem();

  const methods = useForm<LiabilityFormType>({
    resolver: yupResolver(getLiabilityFormSchema(t)),
    mode: "onBlur",
  });

  const { control, handleSubmit, reset } = methods;

  // biome-ignore lint: <Open is required>
  useEffect(() => {
    const defaultValues: Partial<LiabilityFormType> = {
      liabilityType: liabilityDetail?.liabilitySubType,
      name: liabilityDetail?.name,
      value: liabilityDetail?.amount.value,
      note: liabilityDetail?.note ?? undefined,
      institution: liabilityDetail?.institution ?? undefined,
      clientId: liabilityDetail?.clientGuid ?? undefined,
      expenseItemId: liabilityDetail?.relatedExpenseItemGuid ?? undefined,
      dueDate: liabilityDetail?.dueDate || null,
    };
    reset(defaultValues);
  }, [liabilityDetail, open, reset]);

  const liabilityOptions: Option[] = (Object.keys(LiabilityItemLiabilitySubType) as Array<LiabilityItemLiabilitySubType>).map((value) => ({
    value,
    name: t(Resources.AnalysisDetail.Assets.Liabilities.LiabilityType[LIABILITY_SUBTYPE_RESOURCE_KEY[value]]),
  }));

  const loanExpenseOptions: Option[] = [
    { value: "", name: "-" },
    ...(loanExpense?.items.map((expense) => ({
      value: expense.guid || "",
      name: `${expense.name}${expense.institution ? ` - ${expense.institution}` : ""} - ${formatCurrency(expense.amount.value, expense.amount.currencyCode)}/${t(Resources.Common.Month)}`,
      hidden: !(liabilityDetail?.relatedExpenseItemGuid === expense.guid || !expense.relatedAssetItemGuid),
    })) || []),
  ];

  const clientOptions: Option[] = [
    { value: "", name: "-" },
    ...(clientsData?.clients.map((client) => ({
      value: client.guid,
      name: formatClientName(client.firstName, client.surname, client.birthDate ?? null),
    })) || []),
  ];

  const onSubmit = (data: LiabilityFormType) => {
    try {
      const payload = {
        amount: {
          currencyCode: "CZK",
          frequency: AmountFrequency.one_time,
          value: data.value,
        },
        note: data.note || "",
        name: data.name,
        institution: data.institution || "",
        liabilitySubType: data.liabilityType as LiabilityItemLiabilitySubType,
        clientGuid: data.clientId || null,
        relatedExpenseItemGuid: data.expenseItemId || null,
        dueDate: data.dueDate || null,
      };

      if (!liability) {
        create(
          { analysisId, versionId, data: payload },
          {
            onSuccess: () => {
              toast.success(t(Resources.AnalysisDetail.Assets.Liabilities.Created));
              invalidate();
              onClose();
            },
            onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Liabilities.Error)),
          },
        );
      } else {
        update(
          { analysisId, versionId, itemId: liability.guid || "", data: payload },
          {
            onSuccess: () => {
              toast.success(t(Resources.AnalysisDetail.Assets.Liabilities.Edited));
              invalidate();
              queryClient.invalidateQueries({ queryKey: getGetLiabilityItemQueryKey(analysisId, versionId, liability.guid || "") });
              onClose();
            },
            onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Liabilities.Error)),
          },
        );
      }
    } catch {
      toast.error(t(Resources.AnalysisDetail.Assets.Liabilities.Error));
    }
  };

  const onDelete = (relatedExpenseItemGuid?: string) => {
    try {
      deleteItem(
        {
          analysisId,
          versionId,
          itemId: liability?.guid || "",
          params: relatedExpenseItemGuid ? { relatedExpenseItemGuid } : undefined,
        },
        {
          onSuccess: () => {
            toast.success(t(Resources.AnalysisDetail.Assets.Liabilities.Deleted));
            invalidate();
            onClose();
            setDeleteModalOpen(false);
          },
          onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Liabilities.Error)),
        },
      );
    } catch {
      toast.error(t(Resources.AnalysisDetail.Assets.Liabilities.Error));
    }
  };

  return (
    <>
      <FormProvider {...methods}>
        <Drawer
          open={open}
          onClose={onClose}
          closeLabel={t(Resources.Common.Close)}
          title={liability ? t(Resources.AnalysisDetail.Assets.Liabilities.Edit) : t(Resources.AnalysisDetail.Assets.Liabilities.Add)}
          onSubmit={handleSubmit(onSubmit)}
          startAction={
            !!liability && (
              <Button variant="text" color="error" disabled={readOnly} onClick={() => setDeleteModalOpen(true)}>
                {t(Resources.Common.Delete)}
              </Button>
            )
          }
          primaryAction={
            <Button variant="contained" type="submit" disabled={readOnly}>
              {liability ? t(Resources.Common.Edit) : t(Resources.Common.Create)}
            </Button>
          }
        >
          <Grid container spacing={Spacing.XL} py={Spacing.XXL}>
            <Grid size={12}>
              <FormSelect
                control={control}
                name="liabilityType"
                label={t(Resources.AnalysisDetail.Assets.Liabilities.LiabilityType.Title)}
                required
                options={toSelectOptions(liabilityOptions)}
                placeholder={t(Resources.Common.SelectPlaceholder)}
                disabled={readOnly}
                fullWidth
              />
            </Grid>
            <Grid size={12}>
              <FormTextField
                control={control}
                name="name"
                label={t(Resources.AnalysisDetail.Assets.Name)}
                required
                disabled={readOnly}
                fullWidth
              />
            </Grid>
            <Grid size={12}>
              <FormNumberField
                control={control}
                name="value"
                label={t(Resources.AnalysisDetail.Assets.Liabilities.Value)}
                required
                scale={0}
                thousandsSeparator=" "
                unit={t(Resources.Common.CZKSuffix)}
                disabled={readOnly}
                fullWidth
              />
            </Grid>
            {loanExpense?.entryMode === "individual" && (
              <Grid size={12}>
                <FormSelect
                  control={control}
                  name="expenseItemId"
                  label={t(Resources.AnalysisDetail.Assets.Liabilities.RegisteredExpenses)}
                  options={toSelectOptions(loanExpenseOptions)}
                  placeholder={t(Resources.Common.SelectPlaceholder)}
                  disabled={readOnly}
                  fullWidth
                />
              </Grid>
            )}
            <Grid size={6}>
              <FormDatePicker
                control={control}
                name="dueDate"
                label={t(Resources.AnalysisDetail.Assets.Liabilities.DueDate)}
                disabled={readOnly}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid size={6}>
              <FormSelect
                control={control}
                name="clientId"
                label={t(Resources.AnalysisDetail.Assets.Liabilities.BelongsTo)}
                options={toSelectOptions(clientOptions)}
                placeholder={t(Resources.Common.SelectPlaceholder)}
                disabled={readOnly}
                fullWidth
              />
            </Grid>
            <Grid size={12}>
              <FormTextField
                control={control}
                name="institution"
                label={t(Resources.AnalysisDetail.Assets.Liabilities.Insitution)}
                disabled={readOnly}
                fullWidth
              />
            </Grid>
            <Grid size={12}>
              <FormTextField
                control={control}
                name="note"
                label={t(Resources.AnalysisDetail.Goals.GoalNote)}
                maxLength={NOTE_MAX_LENGTH}
                disabled={readOnly}
                multiline
                minRows={6}
                maxRows={8}
                fullWidth
              />
            </Grid>
          </Grid>
        </Drawer>
      </FormProvider>
      {liabilityDetail?.relatedExpenseItemGuid ? (
        <DialogModal
          open={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          closeLabel={t(Resources.Common.Close)}
          title={t(Resources.AnalysisDetail.Budget.Expenses.Form.DeleteExpenseItem)}
          subtitle={t(Resources.AnalysisDetail.Budget.Expenses.Form.DeleteLoanExpenseItemWithoutAssetHint)}
          secondaryAction={
            <Button variant="contained" onClick={() => onDelete(liabilityDetail.relatedExpenseItemGuid ?? undefined)}>
              {t(Resources.AnalysisDetail.Assets.Liabilities.DeleteLiabilityWithLoanExpense)}
            </Button>
          }
          primaryAction={
            <Button variant="contained" onClick={() => onDelete()}>
              {t(Resources.AnalysisDetail.Assets.Liabilities.DeleteLiabilityWithoutLoanExpense)}
            </Button>
          }
        />
      ) : (
        <ConfirmDialog
          open={deleteModalOpen}
          title={t(Resources.AnalysisDetail.Budget.Expenses.Form.DeleteExpenseItem)}
          message={t(Resources.AnalysisDetail.Budget.Expenses.Form.DeleteLoanExpenseItemWithoutAssetHint)}
          confirmLabel={t(Resources.Common.Yes)}
          cancelLabel={t(Resources.Common.Cancel)}
          onConfirm={() => onDelete()}
          onCancel={() => setDeleteModalOpen(false)}
        />
      )}
    </>
  );
};
